import { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { useSocket } from '../../context/SocketContext'
import { useAuth } from '../../context/AuthContext'
import { FiSend, FiUser, FiPaperclip, FiMoreVertical, FiFile, FiImage, FiDownload, FiX, FiCheck } from 'react-icons/fi'
import axios from 'axios'
import './Chat.css'

const Chat = () => {
  const { agentId } = useParams()
  const { agents, messages, sendMessage, isConnected } = useSocket()
  const { user } = useAuth()
  const [input, setInput] = useState('')
  const [selectedAgent, setSelectedAgent] = useState(null)
  const [chatMessages, setChatMessages] = useState([])
  const [agentFilter, setAgentFilter] = useState('')
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(null)
  const fileInputRef = useRef(null)
  const messagesEndRef = useRef(null)

  // Select the agent from URL param or default to Kclaw
  useEffect(() => {
    if (agentId) {
      const agent = agents.find(a => a.agent_id === agentId)
      if (agent) setSelectedAgent(agent)
    } else {
      const kclaw = agents.find(a => a.agent_id === 'kclaw-main-ai')
      if (kclaw) setSelectedAgent(kclaw)
    }
  }, [agentId, agents])

  // Update chat messages when new messages arrive
  useEffect(() => {
    if (!selectedAgent) return
    const convId = `conv-${user?.id}-${selectedAgent.agent_id}`
    const filtered = messages.filter(m => m.conversation_id === convId)
    setChatMessages(filtered)
  }, [messages, selectedAgent, user])

  // Auto scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chatMessages])

  const handleSend = (e) => {
    e.preventDefault()
    if (!input.trim() || !selectedAgent) return

    sendMessage(selectedAgent.agent_id, input.trim())
    setInput('')
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend(e)
    }
  }

  const handleAttachClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0]
    if (!file || !selectedAgent) return

    setUploading(true)
    setUploadProgress({ name: file.name, percent: 0 })

    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('receiver_id', selectedAgent.agent_id)
      formData.append('sender_name', user?.name || 'You')

      const token = localStorage.getItem('token')
      const res = await axios.post('/api/upload/chat', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          setUploadProgress({ name: file.name, percent })
        }
      })

      setUploading(false)
      setUploadProgress(null)
    } catch (err) {
      console.error('Upload failed:', err)
      setUploading(false)
      setUploadProgress(null)
      alert('File upload failed: ' + (err.response?.data?.error || err.message))
    }

    // Reset file input
    e.target.value = ''
  }

  // Filter agents for sidebar
  const filteredAgents = agents.filter(a =>
    a.name?.toLowerCase().includes(agentFilter.toLowerCase())
  )

  return (
    <div className="chat-page">
      {/* Agent sidebar */}
      <div className="chat-sidebar">
        <div className="chat-sidebar-header">
          <h3>Conversations</h3>
          <div className="chat-search">
            <input
              type="text"
              placeholder="Filter agents..."
              value={agentFilter}
              onChange={(e) => setAgentFilter(e.target.value)}
            />
          </div>
        </div>
        <div className="chat-agent-list">
          {filteredAgents.map(agent => (
            <button
              key={agent.agent_id}
              className={`chat-agent-item ${selectedAgent?.agent_id === agent.agent_id ? 'active' : ''}`}
              onClick={() => setSelectedAgent(agent)}
            >
              <div className="chat-agent-avatar">
                <span>{agent.name?.charAt(0) || 'A'}</span>
                <span className={`chat-agent-status ${agent.status}`}></span>
              </div>
              <div className="chat-agent-info">
                <div className="chat-agent-name">{agent.name}</div>
                <div className="chat-agent-role">{agent.role}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Chat main area */}
      <div className="chat-main">
        {selectedAgent ? (
          <>
            {/* Chat header */}
            <div className="chat-header">
              <div className="chat-header-info">
                <div className="chat-header-avatar">
                  <span>{selectedAgent.name?.charAt(0) || 'A'}</span>
                  <span className={`chat-agent-status ${selectedAgent.status}`}></span>
                </div>
                <div>
                  <div className="chat-header-name">{selectedAgent.name}</div>
                  <div className="chat-header-status">
                    <span className={`status-dot ${selectedAgent.status}`}></span>
                    {selectedAgent.status?.charAt(0).toUpperCase() + selectedAgent.status?.slice(1) || 'Offline'}
                  </div>
                </div>
              </div>
              <div className="chat-header-actions">
                <span className="connection-badge">
                  <span className={`status-dot ${isConnected ? 'connected' : 'disconnected'}`}></span>
                  {isConnected ? 'Connected' : 'Disconnected'}
                </span>
              </div>
            </div>

            {/* Messages */}
            <div className="chat-messages">
              {chatMessages.length === 0 ? (
                <div className="chat-empty">
                  <div className="chat-empty-icon">💬</div>
                  <h3>Start a conversation with {selectedAgent.name}</h3>
                  <p>Send a message to begin communicating with {selectedAgent.name}. They can help with tasks, answer questions, and coordinate with other agents.</p>
                </div>
              ) : (
                chatMessages.map((msg, idx) => (
                  <div
                    key={msg.id || idx}
                    className={`message ${msg.sender_type === 'user' ? 'message-user' : 'message-agent'}`}
                  >
                    <div className="message-content">
                      <div className="message-sender">{msg.sender_name}</div>
                      {msg.message_type === 'file' && msg.file_url ? (
                        <div className="message-file">
                          {isImageFile(msg.file_name || msg.file_url) ? (
                            <a href={msg.file_url} target="_blank" rel="noopener noreferrer" className="file-image-link">
                              <img src={msg.file_url} alt={msg.file_name} className="file-preview-image" />
                            </a>
                          ) : (
                            <a href={msg.file_url} target="_blank" rel="noopener noreferrer" className="file-attachment">
                              <span className="file-icon">
                                <FiFile />
                              </span>
                              <span className="file-info">
                                <span className="file-name">{msg.file_name}</span>
                                <span className="file-size">{formatFileSize(msg.file_size)}</span>
                              </span>
                              <span className="file-download">
                                <FiDownload />
                              </span>
                            </a>
                          )}
                        </div>
                      ) : (
                        <div className="message-text">{msg.content}</div>
                      )}
                      <div className="message-time">
                        {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form className="chat-input-area" onSubmit={handleSend}>
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
                disabled={uploading}
              />
              <button
                type="button"
                className={`chat-attach-btn ${uploading ? 'uploading' : ''}`}
                title={uploading ? 'Uploading...' : 'Attach file'}
                onClick={handleAttachClick}
                disabled={uploading || !isConnected}
              >
                {uploading ? <FiCheck /> : <FiPaperclip />}
              </button>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={`Message ${selectedAgent.name}...`}
                rows={1}
                disabled={!isConnected}
              />
              <button
                type="submit"
                className="chat-send-btn"
                disabled={!input.trim() || !isConnected}
                title="Send message"
              >
                <FiSend />
              </button>
            </form>
          </>
        ) : (
          <div className="chat-no-selection">
            <div className="chat-empty-icon">💬</div>
            <h3>Select an agent to chat with</h3>
            <p>Choose an agent from the sidebar to start a conversation</p>
          </div>
        )}
      </div>
      {/* Upload progress bar */}
      {uploadProgress && (
        <div className="upload-progress-bar">
          <div className="upload-progress-info">
            <FiFile size={14} />
            <span>{uploadProgress.name}</span>
          </div>
          <div className="upload-progress-track">
            <div
              className="upload-progress-fill"
              style={{ width: `${uploadProgress.percent}%` }}
            />
          </div>
          <span className="upload-progress-pct">{uploadProgress.percent}%</span>
        </div>
      )}
    </div>
  )
}

// Helper: check if a filename looks like an image
function isImageFile(name) {
  if (!name) return false
  const ext = name.split('.').pop()?.toLowerCase()
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext)
}

// Helper: format bytes to human size
function formatFileSize(bytes) {
  if (!bytes) return ''
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

export default Chat
